import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Box, Card, CardContent, Button, Heading, Text, Badge, Skeleton } from '../components/ui';
import { BookOpen, Globe, Edit, ArrowLeft } from 'lucide-react';
import { BookCard } from '../components/library/BookCard';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { formatBookCountBadge } from './authorUtils';
import './AuthorProfile.css';

interface Author {
  id: string;
  name: string;
  slug: string;
  bio: string | null;
  website_url: string | null;
  created_at: string;
}

interface Book {
  id: string;
  slug: string | null;
  title: string;
  description: string | null;
  cover_image_url: string | null;
  publication_year: number | null;
  status: string;
  book_authors: Array<{
    author_order: number;
    authors: {
      id: string;
      name: string;
      slug: string | null;
      bio: string | null;
    };
  }>;
  book_category_relations?: Array<{
    book_categories: {
      id: string;
      name: string;
      slug: string;
      color: string | null;
    };
  }>;
  book_tag_relations?: Array<{
    book_tags: {
      id: string;
      name: string;
      slug: string;
      color: string | null;
    };
  }>;
}

export const AuthorProfile: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [author, setAuthor] = useState<Author | null>(null);
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = profile?.role === 'admin';

  useEffect(() => {
    if (slug) {
      loadAuthorData();
    }
  }, [slug]);

  const loadAuthorData = async () => {
    try {
      setLoading(true);

      // Załaduj dane autora
      const { data: authorData, error: authorError } = await supabase
        .from('authors')
        .select('*')
        .eq('slug', slug)
        .single();

      if (authorError) throw authorError;
      setAuthor(authorData);

      // Załaduj książki autora
      const { data: booksData, error: booksError } = await supabase
        .from('books')
        .select(`
          *,
          book_authors!inner (
            author_order,
            authors (
              id,
              name,
              slug,
              bio
            )
          ),
          book_category_relations (
            book_categories (
              id,
              name,
              slug,
              color
            )
          ),
          book_tag_relations (
            book_tags (
              id,
              name,
              slug,
              color
            )
          )
        `)
        .eq('book_authors.author_id', authorData.id)
        .order('publication_year', { ascending: false });

      if (booksError) throw booksError;

      // Filter only published books for non-admins
      const filteredBooks = isAdmin 
        ? booksData 
        : booksData.filter(book => book.status === 'published');

      setBooks(filteredBooks || []);
    } catch (error) {
      console.error('Error loading author data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="author-profile-page premium-page-bg">
        <div className="author-profile-container">
          <div className="author-profile-skeleton">
            <div className="skeleton-title" />
            <div className="skeleton-text" />
            <div className="skeleton-text" />
          </div>
        </div>
      </div>
    );
  }

  if (!author) {
    return (
      <div className="author-profile-page premium-page-bg">
        <div className="author-profile-container">
          <div className="empty-state">
            <div className="empty-icon" aria-hidden="true">
              <BookOpen size={64} strokeWidth={1.5} />
            </div>
            <h2>Autor nie znaleziony</h2>
            <p>Nie udało się znaleźć autora o podanym identyfikatorze.</p>
            <Button 
              variant="solid"
              size="3"
              onClick={() => navigate('/biblioteka')}
              aria-label="Wróć do biblioteki"
            >
              Wróć do biblioteki
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="author-profile-page premium-page-bg">
      <div className="author-profile-container">
        {/* Breadcrumb */}
        <Breadcrumbs items={[
          { label: 'Biblioteka', path: '/biblioteka' },
          { label: 'Autorzy', path: '/biblioteka/autorzy' },
          { label: author.name }
        ]} />

        {/* Author Hero Section */}
        <Card variant="glass" className="author-hero-card">
          <CardContent>
            <div className="author-header">
              <div className="author-avatar-wrapper">
                <div className="author-avatar">
                  <BookOpen size={40} strokeWidth={1.5} aria-hidden="true" />
                </div>
              </div>
              
              <div className="author-main-info">
                <div className="author-info">
                  <h1 className="author-name">
                    {author.name}
                  </h1>
                  <div className="author-meta">
                    <span className="author-badge">
                      <BookOpen size={16} aria-hidden="true" />
                      <span>
                        {formatBookCountBadge(books.length)}
                      </span>
                    </span>
                  </div>
                </div>
                
                <Box className="author-actions" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {author.website_url && (
                    <Button
                      variant="outline"
                      size="2"
                      onClick={() => window.open(author.website_url!, '_blank')}
                      aria-label="Otwórz stronę internetową autora"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      <Globe size={18} aria-hidden="true" />
                      <span>Strona internetowa</span>
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="2"
                      onClick={() => navigate(`/biblioteka/autor/${author.id}/edytuj`)}
                      aria-label="Edytuj profil autora"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      <Edit size={18} aria-hidden="true" />
                      <span>Edytuj</span>
                    </Button>
                  )}
                </Box>
              </div>
            </div>

            {author.bio && (
              <div className="author-bio-section">
                <h2 className="author-bio-title">O autorze</h2>
                <p className="author-bio-text">{author.bio}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Books Section */}
        <section className="author-books-section" aria-labelledby="books-heading">
          <h2 id="books-heading" className="section-title">
            Książki autora
          </h2>

          {books.length === 0 ? (
            <Card variant="glass" className="empty-books-card">
              <CardContent className="empty-state-small">
                <Box className="empty-state-icon">
                  <BookOpen size={48} strokeWidth={1.5} aria-hidden="true" />
                </Box>
                <Text size="4" color="soft" className="empty-state-text">
                  Brak książek tego autora w bibliotece.
                </Text>
              </CardContent>
            </Card>
          ) : (
            <div className="author-books-grid">
              {books.map((book, index) => (
                <BookCard 
                  key={book.id} 
                  book={book} 
                  isAdmin={isAdmin}
                  animationDelay={index * 100}
                />
              ))}
            </div>
          )}
        </section>

        {/* Back Button */}
        <Box className="author-back-button">
          <Button
            variant="outline"
            size="3"
            onClick={() => navigate('/biblioteka')}
            aria-label="Wróć do biblioteki"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <ArrowLeft size={18} aria-hidden="true" />
            <span>Wróć do biblioteki</span>
          </Button>
        </Box>
      </div>
    </div>
  );
};

